import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCashRegister } from 'react-icons/fa';
import {
  FiBriefcase,
  FiClipboard,
  FiDollarSign,
  FiHelpCircle,
  FiList,
  FiLogOut,
  FiPackage,
  FiShield,
  FiShoppingCart,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import './css/Home.css';
import './css/ResponsiveDashboard.css';
import useResponsiveTableLabels from './useResponsiveTableLabels';
import {
  MENU_CHANNELS,
  DEFAULT_MENU_CHANNEL,
  DEFAULT_ORDER_CHANNEL,
} from './menuChannels';
import {
  ENTITY_TYPE_STORE,
  getActiveRestaurantId,
  getActiveStoreId,
  getEntityType,
  setBusinessLogoUrl,
} from './entityContext';
import {
  filterMenuChannelsByServices,
  getFilteredRestaurantSidebarItems,
} from './restaurantServiceAccess';
import { filterSidebarItemsByPermission } from './rbacSession';
import {
  RESTAURANT_NAV_CLOSE,
  RESTAURANT_NAV_OPEN,
  RESTAURANT_NAV_TOGGLE,
  RESTAURANT_SIDEBAR_COLLAPSE_TOGGLE,
  notifyBusinessContextChanged,
} from './restaurantNav';
import BusinessAccountMenu from './BusinessAccountMenu';
import PartnerBusinessSwitcher from '../partner/PartnerBusinessSwitcher';
import { supportUrl } from '../constants/appRoutes';
import { performLogout } from '../utils/sessionManager';
import { fetchBusinessProfileMedia } from './profileMediaApi';

const SIDEBAR_COLLAPSED_KEY = 'restaurantSidebarCollapsed';

const SIDEBAR_ICONS = {
  'dine-in': FaCashRegister,
  orders: FiClipboard,
  restaurants: FiList,
  purchase: FiShoppingCart,
  finance: FiDollarSign,
  inventory: FiPackage,
  'user-management': FiUsers,
  hiring: FiUserPlus,
  'business-profile': FiBriefcase,
  roles: FiShield,
  pos: FaCashRegister,
};

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function openSupportWindow() {
  const width = 720;
  const height = 720;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;
  const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`;
  const isStore = getEntityType() === ENTITY_TYPE_STORE;
  const businessName = isStore
    ? localStorage.getItem('storeName') || ''
    : localStorage.getItem('restaurantName') || '';
  const newWindow = window.open(
    supportUrl({ type: 'business', name: businessName }),
    '_blank',
    features
  );
  if (!newWindow) {
    alert('Please allow popups for this website');
  }
}

function handleSignOut() {
  performLogout({ redirectPath: '/partner/login' });
}

export const RESTAURANT_SIDEBAR_ITEMS = [
  ['dine-in', 'POS'],
  ['orders', 'Orders'],
  ['restaurants', 'Menu'],
  ['purchase', 'Purchase'],
  ['finance', 'Finance'],
  ['inventory', 'Inventory'],
  ['user-management', 'User Management'],
  ['roles', 'Roles'],
  ['hiring', 'Hiring'],
  ['business-profile', 'Business Profile'],
];

export const SUPERMARKET_SIDEBAR_ITEMS = [
  ['pos', 'POS'],
  ['orders', 'Orders'],
  ['purchase', 'Purchase'],
  ['inventory', 'Inventory'],
  ['user-management', 'User Management'],
  ['roles', 'Roles'],
  ['hiring', 'Hiring'],
  ['business-profile', 'Business Profile'],
];

const RestaurantDashboardLayout = ({
  activeTab,
  children,
  flush = false,
  menuChannel = DEFAULT_MENU_CHANNEL,
  onSelectMenuChannel,
  orderChannel = DEFAULT_ORDER_CHANNEL,
  onSelectOrderChannel,
  enabledServiceTypes = null,
  servicesLoading = false,
}) => {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const mainRef = useRef(null);
  useResponsiveTableLabels(mainRef);
  const isSupermarketMode = getEntityType() === ENTITY_TYPE_STORE;
  const sidebarItems = filterSidebarItemsByPermission(
    isSupermarketMode
      ? SUPERMARKET_SIDEBAR_ITEMS
      : getFilteredRestaurantSidebarItems(enabledServiceTypes, RESTAURANT_SIDEBAR_ITEMS, {
          servicesLoading,
        })
  );
  const visibleMenuChannels = isSupermarketMode
    ? []
    : filterMenuChannelsByServices(MENU_CHANNELS, enabledServiceTypes, { servicesLoading });

  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen((open) => !open), []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('has-restaurant-dashboard');
    window.scrollTo(0, 0);
    return () => {
      document.documentElement.classList.remove('has-restaurant-dashboard');
      document.documentElement.classList.remove('restaurant-nav-open');
      document.documentElement.classList.remove('restaurant-sidebar-collapsed');
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('restaurant-sidebar-collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    document.documentElement.classList.toggle('restaurant-nav-open', mobileNavOpen);
  }, [mobileNavOpen]);

  useEffect(() => {
    window.addEventListener(RESTAURANT_NAV_OPEN, openMobileNav);
    window.addEventListener(RESTAURANT_NAV_CLOSE, closeMobileNav);
    window.addEventListener(RESTAURANT_NAV_TOGGLE, toggleMobileNav);
    window.addEventListener(RESTAURANT_SIDEBAR_COLLAPSE_TOGGLE, toggleSidebarCollapsed);
    return () => {
      window.removeEventListener(RESTAURANT_NAV_OPEN, openMobileNav);
      window.removeEventListener(RESTAURANT_NAV_CLOSE, closeMobileNav);
      window.removeEventListener(RESTAURANT_NAV_TOGGLE, toggleMobileNav);
      window.removeEventListener(RESTAURANT_SIDEBAR_COLLAPSE_TOGGLE, toggleSidebarCollapsed);
    };
  }, [openMobileNav, closeMobileNav, toggleMobileNav, toggleSidebarCollapsed]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMobileNav();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen, closeMobileNav]);

  useEffect(() => {
    let cancelled = false;
    const entityId = isSupermarketMode ? getActiveStoreId() : getActiveRestaurantId();
    const type = isSupermarketMode ? 'STORE' : 'RESTAURANT';
    if (!entityId) return undefined;
    fetchBusinessProfileMedia(type, entityId)
      .then((payload) => {
        if (cancelled) return;
        const next =
          payload?.logoUrl ||
          payload?.logo?.mediaUrl ||
          '';
        setBusinessLogoUrl(next || null);
        notifyBusinessContextChanged();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSupermarketMode]);

  useEffect(() => {
    closeMobileNav();
  }, [activeTab, menuChannel, orderChannel, closeMobileNav]);

  const handleTabClick = (tab) => {
    const resolveRestaurantId = () =>
      localStorage.getItem('restaurantId') ||
      (() => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || 'null');
          return u?.restaurantId || u?.Restaurant_Id || '';
        } catch {
          return '';
        }
      })();

    const resolveStoreId = () =>
      localStorage.getItem('storeId') ||
      (() => {
        try {
          const u = JSON.parse(localStorage.getItem('user') || 'null');
          return u?.storeId || u?.Store_Id || '';
        } catch {
          return '';
        }
      })();

    if (tab === 'business-profile') {
      if (isSupermarketMode) {
        const storeId = resolveStoreId();
        if (storeId) {
          navigate(`/store/${storeId}?tab=details`);
          return;
        }
      } else {
        const restaurantId = resolveRestaurantId();
        if (restaurantId) {
          navigate(`/restaurant/${restaurantId}?tab=details`);
          return;
        }
      }
      navigate('/business/admin?tab=orders');
      return;
    }
    if (tab === 'orders') {
      navigate(`/business/admin?tab=orders&orderType=${orderChannel}`);
      return;
    }
    navigate(`/business/admin?tab=${tab}`);
  };

  return (
    <div
      className={`restaurant-dashboard${flush ? ' restaurant-dashboard--flush' : ''}${
        mobileNavOpen ? ' restaurant-dashboard--nav-open' : ''
      }${sidebarCollapsed ? ' restaurant-dashboard--sidebar-collapsed' : ''}`}
    >
      <button
        type="button"
        className="restaurant-dashboard-nav-backdrop"
        aria-label="Close navigation menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />

      <aside
        id="restaurant-dashboard-sidebar"
        className="restaurant-dashboard-sidebar"
        aria-label="Restaurant navigation"
      >
        <div className="restaurant-dashboard-sidebar-nav">
          {sidebarItems.map(([id, label]) => {
            const isMenu = id === 'restaurants';
            const isActive = activeTab === id;
            const showMenuSubnav = isMenu && isActive && !isSupermarketMode && !sidebarCollapsed;
            const ItemIcon = SIDEBAR_ICONS[id] || FiList;
            return (
              <React.Fragment key={id}>
                <button
                  type="button"
                  className={`restaurant-dashboard-sidebar-item${
                    isActive ? ' active' : ''
                  }`}
                  onClick={() => handleTabClick(id)}
                  title={label}
                >
                  <ItemIcon className="restaurant-dashboard-sidebar-icon" aria-hidden="true" />
                  <span className="restaurant-dashboard-sidebar-label">{label}</span>
                </button>
                {showMenuSubnav && (
                  <div className="restaurant-dashboard-subnav" role="group" aria-label="Menu channels">
                    {visibleMenuChannels.map((channel) => {
                      const ChannelIcon = channel.icon;
                      return (
                        <button
                          key={channel.key}
                          type="button"
                          className={`restaurant-dashboard-subnav-item${
                            menuChannel === channel.key ? ' active' : ''
                          }`}
                          onClick={() =>
                            onSelectMenuChannel
                              ? onSelectMenuChannel(channel.key)
                              : navigate(`/business/admin?tab=restaurants&channel=${channel.key}`)
                          }
                        >
                          <ChannelIcon className="restaurant-dashboard-subnav-icon" aria-hidden="true" />
                          <span>{channel.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="restaurant-dashboard-sidebar-utility">
          <PartnerBusinessSwitcher variant="sidebar" />
          <button
            type="button"
            className="restaurant-dashboard-sidebar-item restaurant-dashboard-sidebar-support"
            onClick={openSupportWindow}
            title="Support"
          >
            <FiHelpCircle className="restaurant-dashboard-sidebar-icon" aria-hidden="true" />
            <span className="restaurant-dashboard-sidebar-label">Support</span>
          </button>
          <button
            type="button"
            className="restaurant-dashboard-sidebar-item restaurant-dashboard-sidebar-signout"
            onClick={handleSignOut}
            title="Sign out"
          >
            <FiLogOut className="restaurant-dashboard-sidebar-icon" aria-hidden="true" />
            <span className="restaurant-dashboard-sidebar-label">Sign out</span>
          </button>
        </div>

        <div className="restaurant-dashboard-sidebar-footer">
          <BusinessAccountMenu placement="sidebar" />
        </div>
      </aside>

      <main
        ref={mainRef}
        className={`restaurant-dashboard-main rt-scope${
          flush ? ' restaurant-dashboard-main--flush' : ''
        }`}
      >
        {children}
      </main>
    </div>
  );
};

export default RestaurantDashboardLayout;
